import { StandardEvents } from '@shopify/events';

/**
 * ForeverChrist product page helpers.
 *
 * <fc-live-block>     Re-renders its contents from the section HTML Horizon fetches when a
 *                     variant is selected, so Liquid stays the single source of truth.
 * <fc-free-shipping>  Same as fc-live-block, and also refreshes after the cart changes.
 * <fc-size-guide>     Opens the fit finder dialog and selects the recommended size.
 */

class FcLiveBlock extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @param {Document} html */
  replaceFrom(html) {
    const next = html.querySelector(`${this.localName}[data-block-id="${this.dataset.blockId}"]`);
    if (next) this.innerHTML = next.innerHTML;
  }

  /** @param {any} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.html) this.replaceFrom(detail.html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[fc-live-block]', error);
      });
  };
}

class FcFreeShipping extends FcLiveBlock {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  /** @param {any} event */
  #handleCartUpdate = (event) => {
    Promise.resolve(event.promise)
      .catch(() => {})
      .then(() => this.refresh());
  };

  async refresh() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.replaceFrom(html);
    } catch (error) {
      console.warn('[fc-free-shipping]', error);
    }
  }
}

class FcSizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.matrix = (this.dataset.matrix || '')
      .split('|')
      .map((row) => row.split(',').map((size) => size.trim()));
    this.height = Number(this.dataset.defaultHeight || 0);
    this.fit = 0;

    this.addEventListener('click', this.#handleClick);
    // The "Size guide" link rendered in the variant picker's size legend lives outside this element.
    this.section = this.closest('.shopify-section') || document;
    this.section.addEventListener('click', this.#handleExternalOpen);
    this.dialog?.addEventListener('click', (event) => {
      // Clicking the backdrop (the dialog element itself) closes it.
      if (event.target === this.dialog) this.close();
    });
    this.#render();
  }

  disconnectedCallback() {
    this.section?.removeEventListener('click', this.#handleExternalOpen);
  }

  /** @param {MouseEvent} event */
  #handleExternalOpen = (event) => {
    const target = /** @type {HTMLElement} */ (event.target)?.closest('[data-fc-open-size-guide]');
    if (!target) return;
    event.preventDefault();
    // On collection pages the trigger sits in the mobile filter sheet; close it first.
    this.closest('fc-collection')?.removeAttribute('data-sheet-open');
    this.open();
  };

  open() {
    this.dialog?.showModal();
    document.documentElement.classList.add('fc-scroll-lock');
  }

  close() {
    this.dialog?.close();
    document.documentElement.classList.remove('fc-scroll-lock');
  }

  get recommendation() {
    const row = this.matrix[this.fit] || this.matrix[0] || [];
    return row[this.height] || row[row.length - 1] || '';
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target)?.closest('[data-fc-action]');
    if (!(target instanceof HTMLElement)) return;

    switch (target.dataset.fcAction) {
      case 'open':
        this.open();
        break;
      case 'close':
        this.close();
        break;
      case 'height':
        this.height = Number(target.dataset.index);
        this.#render();
        break;
      case 'fit':
        this.fit = Number(target.dataset.index);
        this.#render();
        break;
      case 'select':
        this.#selectSize(this.recommendation);
        this.close();
        break;
    }
  };

  #render() {
    for (const button of this.querySelectorAll('[data-fc-action="height"]')) {
      button.setAttribute('aria-pressed', String(Number(button.dataset.index) === this.height));
    }
    for (const button of this.querySelectorAll('[data-fc-action="fit"]')) {
      button.setAttribute('aria-pressed', String(Number(button.dataset.index) === this.fit));
    }
    for (const node of this.querySelectorAll('[data-fc-recommendation]')) {
      node.textContent = this.recommendation;
    }
    for (const row of this.querySelectorAll('[data-fc-row]')) {
      const match = /** @type {HTMLElement} */ (row).dataset.fcRow?.toLowerCase() === this.recommendation.toLowerCase();
      row.classList.toggle('is-recommended', match);
    }
  }

  /** Clicks the matching size in Horizon's variant picker so all of its update logic runs. */
  #selectSize(size) {
    const section = this.closest('.shopify-section') || document;
    const optionName = (this.dataset.optionName || 'size').toLowerCase();
    const inputs = section.querySelectorAll('variant-picker input[type="radio"]');

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if ((input.dataset.optionName || '').toLowerCase() !== optionName) continue;
      if (input.value.toLowerCase() !== size.toLowerCase()) continue;
      if (!input.checked) input.click();
      return;
    }
  }
}

if (!customElements.get('fc-live-block')) customElements.define('fc-live-block', FcLiveBlock);
if (!customElements.get('fc-free-shipping')) customElements.define('fc-free-shipping', FcFreeShipping);
if (!customElements.get('fc-size-guide')) customElements.define('fc-size-guide', FcSizeGuide);
